<template>
  <SettingCard class="gap-4" :row="true">
    <FlexCol class="align-items-center">
      <FlexCol
        class="justify-content-center align-items-center rounded-circle border position-relative"
        style="width: 6rem; height: 6rem"
        :style="{
          background: background,
        }"
      >
        <IconCheck
          v-if="!imageUrl && !text"
          width="6rem"
          :style="{
            color: textColor,
          }"
        />
        <img
          v-if="imageUrl"
          :src="imageUrl"
          alt="front card image"
          class="rounded-circle"
          style="width: 6rem; height: 6rem; object-fit: cover"
        />
        <p
          v-else-if="text"
          class="text-center fw-500 m-0"
          :style="{
            color: textColor,
            fontSize: hasEmoji(text) ? '44px' : '20px',
          }"
        >
          {{ text }}
        </p>

        <template v-if="emojiPicker">
          <FlexCol
            class="cursor-pointer align-items-center bg-white border border-4 d-flex flex-column justify-content-center position-absolute rounded-circle bottom-0 end-0"
            style="width: 2.5rem; height: 2.5rem"
            @click="openEmojiPicker = !openEmojiPicker"
          >
            <img src="@/assets/images/svg/edit-pencil.svg" alt="pencil-icon" />
          </FlexCol>
          <i
            v-show="openEmojiPicker"
            :id="`picker-${id}`"
            class="position-absolute start-0 pb-4"
            style="top: 100px"
          ></i>
        </template>
      </FlexCol>

      <span
        v-if="enableReset"
        class="link-primary text-decoration-underline cursor-pointer"
        @click="emit('update-image', null), emit('update-text', '')"
        >Use Original</span
      >
    </FlexCol>
    <FlexCol class="gap-2 flex-grow-1">
      <button
        class="btn btn-outline-primary"
        @click="() => uploadImageComponent.uploadNewImg()"
      >
        Upload New Image
      </button>
      <button class="btn btn-outline-light" @click="emit('use-logo')">
        {{ changeToText ? "Change to Text" : "Use Logo" }}
      </button>
    </FlexCol>
  </SettingCard>

  <ModalContainer
    :id="`image-picker-modal-${id}`"
    animation="fade"
    keyboard="false"
    backdrop="static"
  >
    <ModalDialog>
      <ModalContent>
        <ModalBody class="p-4 position-relative">
          <div class="mt-4 w-100">
            <UploadImageComponent
              ref="uploadImageComponent"
              :id="id"
              :use-cropped-image="true"
              :image-required="false"
              :aspect-ratio="aspectRatio"
              @uploaded-image="
                (value) => {
                  imagePickerModalComponent.hide();
                  emit('update-image', value.image || props.imageUrl);
                  uploadImageComponent.removeCroppedImage();
                  if (
                    typeof props.imageUrl === 'string' &&
                    props.imageUrl.includes('http')
                  ) {
                    useStampCardStore().frontUseLogo = true;
                  }
                }
              "
              @crop-mode="
                (value) => {
                  if (value) imagePickerModalComponent.show();
                }
              "
            />
          </div>
        </ModalBody>
      </ModalContent>
    </ModalDialog>
  </ModalContainer>
</template>
<script setup>
import FlexCol from "@/layout/FlexCol.vue";
import IconCheck from "@/assets/icons/IconCheck.vue";
import SettingCard from "../Components/SettingCard.vue";
import UploadImageComponent from "../../MemberPortal/Modals/UploadImageComponent.vue";
import { onMounted, ref } from "vue";
import ModalContainer from "@/components/uielements/modal/ModalContainer.vue";
import ModalContent from "@/components/uielements/modal/ModalContent.vue";
import ModalBody from "@/components/uielements/modal/ModalBody.vue";
import ModalDialog from "@/components/uielements/modal/ModalDialog.vue";
import { Modal } from "bootstrap";
import { Picker } from "emoji-mart";
import { useStampCardStore } from "@/stores/stampCard";
import hasEmoji from "@/utilities/hasEmoji";

const props = defineProps({
  id: {
    type: String,
    default: "1",
  },
  background: {
    type: String,
    required: true,
  },
  textColor: {
    type: String,
    required: true,
  },
  imageUrl: {
    type: [String, File],
  },
  text: {
    type: String,
  },
  emojiPicker: {
    type: Boolean,
    default: true,
  },
  changeToText: {
    type: Boolean,
    default: false,
  },
  enableReset: {
    type: Boolean,
    default: false,
  },
  aspectRatio: {
    type: Number,
    default: 1,
  },
});

const emit = defineEmits(["use-logo", "update-image", "update-text"]);
const uploadImageComponent = ref(null);
const imagePickerModalComponent = ref(null);
const openEmojiPicker = ref(false);

onMounted(() => {
  imagePickerModalComponent.value = new Modal(
    `#image-picker-modal-${props.id}`
  );

  if (props.emojiPicker) {
    initPicker();
  }
});

const initPicker = () => {
  const picker = document.querySelector(`#picker-${props.id}`);
  const em = picker.querySelector("em-emoji-picker");

  if (em) {
    em.remove();
  }

  new Picker({
    parent: picker,
    theme: "light",
    previewPosition: "none",
    maxFrequentRows: 0,
    onEmojiSelect: (emoji) => {
      emit("update-text", emoji.native);
      openEmojiPicker.value = false;
    },
  });
};
</script>
<style>
em-emoji-picker {
  --rgb-accent: 255, 0, 0;
  max-height: 16.5rem;
  z-index: 100;
  position: absolute;
}
</style>
